import { Injectable } from '@angular/core';
import { Widget } from '../../../../features/widgets/models/widget';
import { WidgetName } from '../../../../features/widgets/models/widget-name';
import { MockWidgets } from './mock-widgets.const';
import { ChartType } from '../../../../features/widgets/models/widget-chart';

@Injectable({ providedIn: 'root' })
export class MockContentGeneratorService {
  generateMockContentFor(widget: Widget): Widget {
    const mockWidget = this.cloneWidget(
      MockWidgets.find((candidate) => candidate.widgetName === widget.widgetName) ?? widget,
    );

    switch (widget.widgetName) {
      case WidgetName.BirthdayList:
        return {
          ...mockWidget,
          illustrationType: 'themed-image',
          themedImageKey: 'birthdays',
          showHeader: true,
          showFooter: false,
          showCloseButton: true,
          tables: this.withRows(mockWidget.tables, this.getBirthdayRows()),
        };
      case WidgetName.UpcomingReviews:
        return {
          ...mockWidget,
          illustrationType: 'themed-image',
          themedImageKey: 'reviews',
          showHeader: true,
          showFooter: false,
          showCloseButton: true,
          tables: this.withRows(mockWidget.tables, this.getReviewRows()),
        };
      case WidgetName.ClientSpecialOccasions:
        return {
          ...mockWidget,
          illustrationType: 'icon',
          icon: 'celebration',
          showHeader: true,
          showFooter: false,
          showCloseButton: true,
          tables: this.withRows(mockWidget.tables, this.getClientSpecialOccasionRows()),
        };
      case WidgetName.PerformanceOverview:
        return {
          ...mockWidget,
          illustrationType: 'none',
          showHeader: true,
          showFooter: false,
          showCloseButton: true,
          charts: [{
            orderIndex: 0,
            type: ChartType.Bar,
            data: {
              labels: ['Q1', 'Q2', 'Q3', 'Q4'],
              values: [120, 180, 90, 140],
            },
          }],
        };
      case WidgetName.ClientTypeAnalysis:
        return {
          ...mockWidget,
          illustrationType: 'none',
          showHeader: true,
          showFooter: false,
          showCloseButton: true,
          charts: [{
            orderIndex: 0,
            type: ChartType.Pie,
            data: {
              labels: ['Retail', 'Corporate', 'SMB', 'Enterprise'],
              values: [45, 25, 20, 10],
            },
          }],
        };
      case WidgetName.JeanetteMarais:
        return {
          ...mockWidget,
          illustrationType: 'none',
          showHeader: true,
          showFooter: false,
          showCloseButton: true,
          image: {
            src: 'https://encrypted-tbn2.gstatic.com/images?q=tbn:ANd9GcSwaKFQ9DiCDbdJXJRXoPUjxMJZO6n-4ON7xUHpj9JQWMXnR1D0Y3_ojPB1d3BF92Dm6Rgvzw9giKCY3xVlrZPvowJI2FOAYhURGiRdjG76',
            alt: 'Jeanette Marais',
            position: 'top',
            height: '90px',
          },
          body: 'Jeanette Marais is a seasoned marketing professional with over 15 years of experience in digital marketing, brand management, and market research. She has a proven track record of developing and executing successful marketing strategies that drive business growth and enhance brand visibility. Jeanette is passionate about leveraging data-driven insights to optimize marketing campaigns and deliver measurable results. In her free time, she enjoys hiking, photography, and exploring new cuisines.',
        };
      case WidgetName.BigCombo:
        return {
          ...mockWidget,
          illustrationType: 'none',
          showHeader: true,
          showFooter: false,
          showCloseButton: true,
          body: 'This card combines multiple content types including text, image, table, and chart to provide a comprehensive overview of various data points and insights in one place.',
          tables: this.withRows(mockWidget.tables, this.getBigComboRows()),
          charts: [{
            orderIndex: 0,
            type: ChartType.Line,
            data: {
              labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
              values: [200, 250, 300, 280, 320, 400],
            },
          }],
        };
      case WidgetName.PendingServiceAgreements:
        return {
          ...mockWidget,
          illustrationType: 'none',
          showHeader: true,
          showFooter: false,
          showCloseButton: true,
          body: 'The following service agreements are pending approval.',
          tables: this.withRows(mockWidget.tables, this.getPendingServiceAgreementRows()),
        };
      case WidgetName.Leads:
        return {
          ...mockWidget,
          illustrationType: 'none',
          showHeader: true,
          showFooter: false,
          showCloseButton: true,
          body: 'The following leads need to be followed up.',
          tables: this.withRows(mockWidget.tables, this.getLeadRows()),
        };
      default:
        return mockWidget;
    }
  }

  private withRows(tables: Widget['tables'], rows: object[]): Widget['tables'] {
    return (tables ?? []).map((table) => ({
      ...table,
      dataset: rows,
    }));
  }

  private cloneWidget(widget: Widget): Widget {
    return {
      ...widget,
      tables: widget.tables?.map((table) => ({
        ...table,
        columns: table.columns?.map((column) => ({
          ...column,
          actions: column.actions?.map((action) => ({ ...action })) ?? [],
          stylingRules: column.stylingRules?.map((stylingRule) => ({
            orderIndex: stylingRule.orderIndex,
            condition: {
              ...stylingRule.condition,
              values: [...stylingRule.condition.values],
            },
            style: { ...stylingRule.style },
          })),
        })) ?? [],
        dataset: table.dataset ? [...(table.dataset as object[])] : undefined,
      })),
      charts: widget.charts ? [...widget.charts] : undefined,
      image: widget.image ? { ...widget.image } : undefined,
    };
  }

  private getBirthdayRows(): object[] {
    return [
      {
        clientName: 'Lethabo Mokoena',
        birthdate: new Date(1988, 4, 28),
        contactNumber: '+27 82 555 1001',
        emailAddress: 'lethabo.mokoena@example.com',
      },
      {
        clientName: 'Anita van Wyk',
        birthdate: new Date(1979, 4, 30),
        contactNumber: '+27 83 555 1002',
        emailAddress: 'anita.vanwyk@example.com',
      },
      {
        clientName: 'Sipho Khumalo',
        birthdate: new Date(1991, 5, 2),
        contactNumber: '+27 84 555 1003',
        emailAddress: 'sipho.khumalo@example.com',
      },
      {
        clientName: 'Megan Botha',
        birthdate: new Date(1985, 5, 4),
        contactNumber: '+27 72 555 1004',
        emailAddress: 'megan.botha@example.com',
      },
    ];
  }

  private getReviewRows(): object[] {
    return [
      {
        clientName: 'Global Corp',
        reviewDate: new Date(2026, 4, 29),
        status: 'Upcoming',
        reviewId: 'review-1001',
      },
      {
        clientName: 'Jane Smith',
        reviewDate: new Date(2026, 4, 30),
        status: 'Overdue',
        reviewId: 'review-1002',
      },
      {
        clientName: 'Tech Innovations',
        reviewDate: new Date(2026, 5, 2),
        status: 'Upcoming',
        reviewId: 'review-1003',
      },
      {
        clientName: 'Creative Solutions',
        reviewDate: new Date(2026, 5, 3),
        status: 'Overdue',
        reviewId: 'review-1004',
      },
      {
        clientName: 'Bright Future Ltd',
        reviewDate: new Date(2026, 5, 5),
        status: 'Upcoming',
        reviewId: 'review-1005',
      },
    ];
  }

  private getClientSpecialOccasionRows(): object[] {
    return [
      {
        clientName: 'Bob Hope',
        occasion: 'Birthday',
        detail: '34 years old',
        clientKey: 'client-2001',
      },
      {
        clientName: 'Dylan Jester',
        occasion: 'Wedding anniversary',
        detail: '12 years',
        clientKey: 'client-2002',
      },
      {
        clientName: 'Lilian Smith',
        occasion: "Son's birthday",
        detail: 'Kevin - 10 years old',
        clientKey: 'client-2003',
      },
      {
        clientName: 'Acme Ltd',
        occasion: 'Corporate anniversary',
        detail: '5 years',
        clientKey: 'client-2004',
      },
      {
        clientName: 'Blue Co',
        occasion: 'Founding Day',
        detail: '20 years',
        clientKey: 'client-2005',
      },
    ];
  }

  private getBigComboRows(): object[] {
    return [
      {
        metric: 'Revenue',
        value: '$1,200,000',
        change: '+10%',
      },
      {
        metric: 'Customers',
        value: '3,500',
        change: '+5%',
      },
      {
        metric: 'Churn Rate',
        value: '2%',
        change: '-0.5%',
      },
    ];
  }

  private getPendingServiceAgreementRows(): object[] {
    return [
      {
        clientName: 'Nicanor Aakster',
        clientType: 'NATURAL',
        status: 'Pending',
        clientKey: '83ea9bae-8d62-4771-81c5-6784bf3d8883',
      },
      {
        clientName: 'Arcangelo Abbatemarco',
        clientType: 'NATURAL',
        status: 'Rejected',
        clientKey: 'd44339f3-4ac9-4fa2-a40f-6a1d6faf3768',
      },
      {
        clientName: 'Abbott - Jakubowski',
        clientType: 'JURISTIC',
        status: 'Pending',
        clientKey: '92000c84-6706-4a37-a6a5-1b9acf524fca',
      },
    ];
  }

  private getLeadRows(): object[] {
    return [
      {
        leadName: 'Alexandra Blanda',
        status: 'Attempted Contact',
        leadId: '180162',
      },
      {
        leadName: 'Davon Greenfelder',
        status: 'Attempted Contact',
        leadId: '180077',
      },
    ];
  }
}
